import { ReactElement, ReactNode } from 'react';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Card, ICardProps } from '@/common/components/Card/Card';
import { Heading } from '@/common/components/Heading/Heading';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { TIconProps } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { tv } from '@/common/theme/tv.config';
import { TColors } from '@coverflex-tech/hypernova-design-tokens';

export function CardAction({
  className,
  plain,
  iconBadgeColor = 'neutral30',
  title,
  description,
  action,
  onPress,
  backgroundColor,
  icon,
  border,
  size = 'sm',
  disabled,
  illustration,
  children,
}: TCardActionProps) {
  const cardDescription =
    typeof description === 'string' ? (
      <BodyText size="sm" color={backgroundColor ? 'neutral30' : 'neutral20'}>
        {description}
      </BodyText>
    ) : (
      description
    );

  return (
    <Card
      onPress={!disabled ? onPress : undefined}
      className={styles.root({ size, className })}
      plain={plain || disabled}
      backgroundColor={backgroundColor}
      border={border}
    >
      <Stack gap="md">
        <Stack horizontal gap="md" align="center">
          {icon && (
            <IconBadge
              className={styles.badge}
              ratio={size === 'sm' ? 48 : 56}
              icon={
                <icon.type
                  {...icon.props}
                  size={size === 'sm' ? 16 : 20}
                  color={disabled ? 'neutral20' : icon.props.color}
                />
              }
              backgroundColor={iconBadgeColor}
            />
          )}
          <Stack grow className={styles.content} gap="xs">
            <Heading level={5} color={disabled ? 'neutral20' : 'neutral30'}>
              {title}
            </Heading>
            {cardDescription}
          </Stack>
          {illustration && (
            <View className={styles.illustrationWrapper({ size })}>
              <View className={styles.illustration}>{illustration.asset}</View>
            </View>
          )}
          <View className={styles.action}>{action}</View>
        </Stack>
        {children}
      </Stack>
    </Card>
  );
}

const styles = {
  root: tv({
    base: '',
    variants: {
      size: {
        sm: 'p-md md:p-md',
        md: '',
      },
    },
  }),
  badge: 'flex-shrink-0',
  content: 'relative z-[1] min-w-0',
  action: 'ml-auto relative',
  illustrationWrapper: tv({
    base: 'relative self-stretch',
    variants: {
      size: {
        sm: 'w-[6rem] scale-[0.55]',
        md: 'w-[6rem] scale-[0.55] md:w-[8rem] md:scale-[0.70]',
      },
    },
  }),
  illustration:
    'absolute transform left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
};

export type TCardActionProps = {
  size?: ICardProps['size'];
  iconBadgeColor?: keyof TColors['light']['semantic']['surface'];
  icon?: ReactElement<TIconProps>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  plain?: boolean;
  className?: string;
  children?: ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  backgroundColor?: keyof TColors['light']['semantic']['surface'];
  border?: boolean;
  illustration?: {
    asset: ReactNode;
    offsetX?: number;
    offsetY?: number;
  };
};
